import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Download as DownloadIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { Attachment, AttachmentDocumentKind, AttachmentEntityType, AttachmentService } from '../../services/attachment.service';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';

export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface Props {
  entityType: AttachmentEntityType;
  entityId: string;
  canManage: boolean;
  /** S4: when given, each file carries a document kind picked from this list (customer / lead / opportunity). */
  kinds?: { value: AttachmentDocumentKind; label: string }[];
}

const AttachmentsPanel: React.FC<Props> = ({ entityType, entityId, canManage, kinds }) => {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploadKind, setUploadKind] = useState<AttachmentDocumentKind>('OTHER');
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState<Attachment | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setItems(await AttachmentService.list(entityType, entityId));
    } catch (e) {
      toast.showApiError(e, 'Failed to load attachments');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > MAX_ATTACHMENT_BYTES) {
      toast.showError('That file is larger than 5 MB. Please choose a smaller PDF or image.');
      return;
    }
    setUploading(true);
    try {
      await AttachmentService.upload(entityType, entityId, file, ...(kinds ? [uploadKind] : []));
      toast.showSuccess('Attachment uploaded');
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to upload attachment');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (a: Attachment) => {
    try {
      saveBlob(await AttachmentService.download(a.id), a.fileName);
    } catch (err) {
      toast.showApiError(err, 'Failed to download attachment');
    }
  };

  const handleKindChange = async (a: Attachment, kind: AttachmentDocumentKind) => {
    try {
      await AttachmentService.updateKind(a.id, kind);
      setItems(prev => prev.map(x => (x.id === a.id ? { ...x, documentKind: kind } : x)));
    } catch (err) {
      toast.showApiError(err, 'Failed to change the document kind');
    }
  };

  const kindLabel = (k?: AttachmentDocumentKind | null) => kinds?.find(x => x.value === k)?.label ?? '-';

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await AttachmentService.remove(toDelete.id);
      toast.showSuccess('Attachment deleted');
      setToDelete(null);
      await load();
    } catch (err) {
      toast.showApiError(err, 'Failed to delete attachment');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }} data-help-section="common.attachments">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1 }}>
        <Typography variant="h6">Attachments</Typography>
        {canManage && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            {kinds && (
              <Select
                size="small"
                data-help="common.attachments.field.kind"
                value={uploadKind}
                onChange={e => setUploadKind(e.target.value as AttachmentDocumentKind)}
                inputProps={{ 'aria-label': 'Document kind for the next upload' }}
                sx={{ minWidth: 160, flex: '1 1 160px', maxWidth: 220 }}
              >
                {kinds.map(k => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
              </Select>
            )}
            <input
              ref={fileInput}
              type="file"
              hidden
              data-testid="attachment-file-input"
              accept="application/pdf,image/png,image/jpeg"
              onChange={handleFile}
            />
            <Button
              data-help="common.attachments.action.upload"
              variant="outlined"
              size="small"
              startIcon={uploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
              disabled={uploading}
              onClick={() => fileInput.current?.click()}
            >
              Upload file
            </Button>
          </Box>
        )}
      </Box>
      {loading ? (
        <CircularProgress size={20} />
      ) : items.length === 0 ? (
        <Typography color="text.secondary">No attachments.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="common.attachments.col.file">File</TableCell>
                {kinds && <TableCell data-help="common.attachments.col.kind">Kind</TableCell>}
                <TableCell data-help="common.attachments.col.size">Size</TableCell>
                <TableCell data-help="common.attachments.col.by">Uploaded by</TableCell>
                <TableCell data-help="common.attachments.col.date">Date</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(a => (
                <TableRow key={a.id}>
                  <TableCell sx={{ wordBreak: 'break-word' }}>{a.fileName}</TableCell>
                  {kinds && (
                    <TableCell>
                      {canManage ? (
                        <Select
                          size="small"
                          variant="standard"
                          data-help="common.attachments.field.rowKind"
                          value={a.documentKind ?? 'OTHER'}
                          onChange={e => handleKindChange(a, e.target.value as AttachmentDocumentKind)}
                          inputProps={{ 'aria-label': `Document kind of ${a.fileName}` }}
                        >
                          {kinds.map(k => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
                        </Select>
                      ) : kindLabel(a.documentKind)}
                    </TableCell>
                  )}
                  <TableCell>{formatSize(a.sizeBytes)}</TableCell>
                  <TableCell>{a.uploadedByName ?? '-'}</TableCell>
                  <TableCell>{a.createdAt ? formatDate(a.createdAt) : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton data-help="common.attachments.action.download" size="small" aria-label={`Download ${a.fileName}`} onClick={() => handleDownload(a)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    {canManage && (
                      <IconButton data-help="common.attachments.action.delete" size="small" aria-label={`Delete ${a.fileName}`} onClick={() => setToDelete(a)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        PDF, PNG or JPEG, up to 5 MB.
      </Typography>

      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} data-help-page="common.attachments.deleteDialog.page">
        <DialogTitle>Delete attachment?</DialogTitle>
        <DialogContent>
          <Typography>{toDelete?.fileName} will be removed.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToDelete(null)} disabled={deleting} data-help="common.attachments.action.cancelDelete">Cancel</Button>
          <Button data-help="common.attachments.action.confirmDelete" color="error" variant="contained" onClick={handleDelete} disabled={deleting}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AttachmentsPanel;
